import i18next from 'i18next';
import { isCNP as validateCnp, isCIF as validateCif } from '@totalsoft/validations';
import { Validator, Success, Failure, ValidationError } from '@totalsoft/pure-validations';
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { any } from 'ramda';
import moment from 'moment';

export const defaultFormatDateTime = date => moment(date).format('DD-MM-YYYY');

export function lessThanOrEqualDate(max) {
  return Validator(function lessThanOrEqualDate(value) {
    return value === null || value === undefined || moment(value).isSameOrBefore(max, 'day')
      ? Success
      : Failure(ValidationError(i18next.t('Validations.Generic.LessOrEqual', { max: max |> defaultFormatDateTime })));
  });
}

export function lessThanDate(max) {
  return Validator(function lessThanDate(value) {
    return value === null || value === undefined || moment(value).isBefore(max, 'day')
      ? Success
      : Failure(ValidationError(i18next.t('Validations.Generic.Less', { max: max |> defaultFormatDateTime })));
  });
}

export function greaterThanOrEqualDate(min) {
  return Validator(function greaterThanOrEqualDate(value) {
    return value === null || value === undefined || moment(value).isSameOrAfter(min, 'day')
      ? Success
      : Failure(ValidationError(i18next.t('Validations.Generic.GreaterOrEqual', { min: min |> defaultFormatDateTime })));
  });
}

export function greaterThanDate(min) {
  return Validator(function greaterThanDate(value) {
    return value === null || value === undefined || moment(value).isAfter(min, 'day')
      ? Success
      : Failure(ValidationError(i18next.t('Validations.Generic.Greater', { min: min |> defaultFormatDateTime })));
  });
}

export const isPhone = countryCode =>
  Validator(value => {
    let phoneNumber = parsePhoneNumber(value);
    if (phoneNumber === undefined) phoneNumber = parsePhoneNumber(value, countryCode);
    return !value || isValidPhoneNumber(phoneNumber?.number ?? value)
      ? Success
      : Failure(ValidationError(i18next.t('Validations.Generic.Phone')));
  });

export const noSpaceAtBeginning = Validator(function noSpaceAtBeginning(x) {
  const input = /^[^-\s]([a-zA-Z0-9])/;
  return input.test(String(x)) ? Success : Failure(ValidationError(i18next.t('Validations.Generic.NoSpace')));
});

export const isCnp = Validator(function isCnp(x) {
  return validateCnp(x) ? Success : Failure(ValidationError(i18next.t('Validations.Generic.CNP')));
});

export const isCif = Validator(function isCif(z) {
  return validateCif(z) ? Success : Failure(ValidationError(i18next.t('Validations.Generic.CIF')));
});

export const isCnpOrCif = Validator(function isCnpOrCif(x) {
  return validateCnp(x) || validateCif(x) ? Success : Failure(ValidationError(i18next.t('Validations.Generic.UniqueId')));
});

export const isSeriesAndNumber = Validator(function isSeriesAndNumber(x) {
  const input = /^[A-Za-z]{2}\d{6}$/;
  return input.test(String(x)) ? Success : Failure(ValidationError(i18next.t('Validations.Generic.SeriesAndNumber')));
});

export const isCIVSeries = Validator(x => {
  const input = /^[A-Z]{1}\d{6}$/;
  return input.test(String(x)) ? Success : Failure(ValidationError(i18next.t('Validations.Generic.CIVSeries')));
});

export const dateIsGreaterThanOrEqualToCurrentDate = Validator(function dateIsGreaterThanOrEqualToCurrentDate(x) {
  return moment(x).isSameOrAfter(new moment().format('YYYY-MM-DD'))
    ? Success
    : Failure(ValidationError(i18next.t('Validations.Generic.DateGreaterThanOrEqualToCurrentDate')));
});

export const dateIsLessThanOrEqualToCurrentDate = Validator(function dateIsLessThanOrEqualToCurrentDate(x) {
  const date = new moment(x).format('YYYY-MM-DD');
  if (x) {
    return moment(date).isSameOrBefore(new moment().format('YYYY-MM-DD'))
      ? Success
      : Failure(ValidationError(i18next.t('Validations.Generic.DateLessThanOrEqualToCurrentDate')));
  }
  return Success;
});

export const dateIsGreaterThanGivenDate = (date, givenDateName) =>
  Validator(function dateIsGreaterThanGivenDate(x) {
    if (date && x) {
      return moment(x).isAfter(new Date(date), 'day')
        ? Success
        : Failure(ValidationError(i18next.t('Validations.Generic.DateGreaterThanGivenDate', { givenDateName })));
    }
    return Success;
  });

export const dateIsGreaterThanOrEqualToGivenDate = (date, givenDateName) =>
  Validator(function dateIsGreaterThanOrEqualToGivenDate(x) {
    return moment(x).isSameOrAfter(new Date(date), 'day')
      ? Success
      : Failure(ValidationError(i18next.t('Validations.Generic.DateGreaterThanOrEqualToGivenDate', { givenDateName })));
  });

export const isIdSeries = Validator(function isIdSeries(x) {
  var input = /^[A-Za-z]{2}$/;
  return input.test(String(x)) ? Success : Failure(ValidationError(i18next.t('Validations.IdSeries')));
});

export const onlyLetters = Validator(function onlyLetters(x) {
  var input = /^[\p{Letter} -']+$/u;
  return input.test(String(x)) ? Success : Failure(ValidationError(i18next.t('Validations.OnlyLetters')));
});

export const isSixNumberCharacters = Validator(function isSixNumberCharacters(x) {
  var input = /^[0-9]{6}$/;
  return input.test(String(x)) ? Success : Failure(ValidationError(i18next.t('Validations.SixNumberCharacters')));
});

export const isPhotoUploaded = Validator(function isPhotoUploaded(input) {
  return input ? Success : Failure(ValidationError(i18next.t('Validations.IdPhotoMandatory')));
});

export const singleSelectionValidator = Validator(list => {
  if (list === null || list === undefined) {
    return Success;
  }
  if (list.every(l => l.isMainActor === false)) {
    return Failure(ValidationError(i18next.t('Validations.TrueValueRequired')));
  } else if (list.filter(l => l.isMainActor === true).length > 1) {
    return Failure(ValidationError(i18next.t('Validations.SingleIsMainSelection')));
  } else {
    return Success;
  }
});

export const requiredFile = Validator(x =>
  x && x.fileId && x.fileName ? Success : 'Validations.FileIsMandatory' |> i18next.t |> ValidationError |> Failure
);

export const integer = Validator(x => (Number.isInteger(x) ? Success : 'Validations.Integer' |> i18next.t |> ValidationError |> Failure));

export const numberOfFiles = Validator(list =>
  list.length > 5 ? Failure(ValidationError(i18next.t('Validations.MaximumNumberOfFiles'))) : Success
);

export const hasFixedLength = (input, length) =>
  Validator(function hasLength(input) {
    var myRegExp = new RegExp('^[a-zA-Z0-9]{' + length + '}$');
    return myRegExp.test(input) ? Success : Failure(ValidationError(i18next.t('Validations.Generic.OnlyLettersAndNumbers', { length })));
  });

export const hasCapitalLettersAndNumbers = Validator(function hasCapitalLettersAndNumbers(x) {
  var input = /^[A-Z0-9]+$/;
  return input.test(String(x)) ? Success : Failure(ValidationError(i18next.t('Validations.Generic.OnlyCapitalLettersAndNumbers')));
});

export const isCnpUnique = (list, personId) =>
  Validator(function isCnpUnique(cnp) {
    return list |> any(item => item.cnp === cnp && item.id !== personId)
      ? Failure(ValidationError(i18next.t('Validations.UniqueCnp')))
      : Success;
  });

export const isFlowNameUnique = (list, flowId) =>
  Validator(function isFlowNameUnique(name) {
    return list |> any(item => item.name === name && item.id !== flowId)
      ? Failure(ValidationError(i18next.t('Validations.UniqueFlowName')))
      : Success;
  });

/**
 * A validator that checks if a value is empty (null, undefined or '')
 */
export const empty = Validator(x => (x == null || x === '' ? Success : Failure(ValidationError(i18next.t('Validations.Generic.Empty')))));

export const isMonth = Validator(function isMonth(month) {
  return month >= 1 && month <= 12 ? Success : Failure(ValidationError(i18next.t('Validations.Generic.Month')));
});

export const isPositive = Validator(x => {
  if (x == null) return Success;

  return x > 0 ? Success : Failure(ValidationError(i18next.t('Validations.Generic.Positive')));
});

export const isPositiveOrZero = Validator(x => {
  if (x == null) return Success;

  return x >= 0 ? Success : Failure(ValidationError(i18next.t('Validations.Generic.PositiveOrZero')));
});

export const manufacturingYearAssetValidator = Validator(x => {
  if (!x) return Success;

  return x >= 1990 && x <= new Date().getFullYear() + 1
    ? Success
    : Failure(
        ValidationError(
          i18next.t('Validations.Generic.ManufacturingYearAsset', {
            currentYearPlusOne: new Date().getFullYear() + 1
          })
        )
      );
});

export const validateDistanceBetweenTwoDates = (startDate, startDateName, endDateName, days) =>
  Validator(function validateDistanceBetweenTwoDates(endDate) {
    if (startDate && endDate) {
      var a = moment(startDate, 'YYYY-MM-DD');
      var b = moment(endDate, 'YYYY-MM-DD');
      var period = b.diff(a, 'days');

      return period >= days
        ? Success
        : Failure(
            ValidationError(
              i18next.t('Validations.Generic.DiffBetweenTwoDates', {
                startDateName,
                endDateName,
                days
              })
            )
          );
    }
    return Success;
  });
